import { ApiResponse, User, AuthUser, AuctionWithItems, AuctionAccessResponse, ItemWithBids, Account, SSLCertificate, PaymentMethod, Invoice, Transaction, PaymentAttempt, UserRole, TenantInfo, UserAuction, UserAuctionWithDetails, Item, TrackingSettings, MarketingSettings, MarketingAsset, MarketingAssetWithContext, MarketingChannel, MarketingImageQuality, DonationQuote, AccountBidder, AccountBidderDirectory, ItemOffer, OfferForRecipient } from '../types';
import { MagicLinkRequestData, CreateAuctionData, UpdateAuctionData, CreateItemData, UpdateItemData, CreateBidData } from '../schemas';

/**
 * The page the visitor is on, for recording which auctioneer introduced them.
 *
 * Sign-up and sign-in carry the page they will return to, which is where the
 * visitor actually came from, so that is preferred over the auth page itself.
 */
const currentSignupPath = (): string | undefined => {
  try {
    const { pathname, search } = window.location;
    return `${pathname}${search}`;
  } catch {
    // No window to read - nothing is lost but the attribution.
    return undefined;
  }
};

// Dynamic API base URL that works with custom domains
export const API_BASE_URL = (() => {
  // In development, always use localhost
  if (process.env.NODE_ENV !== 'production') {
    return 'http://localhost:5001/api';
  }
  
  // In production, check if we're on a custom domain
  const currentDomain = window.location.hostname;
  const mainDomains = [
    'localhost',
    'charity-auctions-web-app.onrender.com',
    'auctions.decoded.digital',
  ];
  
  // If we're on a custom domain, use the current domain for API calls
  if (!mainDomains.includes(currentDomain)) {
    return `${window.location.origin}/api`;
  }
  
  // If we're on the main domain, use the configured API base URL or current origin
  return process.env.REACT_APP_API_BASE_URL 
    ? `${process.env.REACT_APP_API_BASE_URL}/api`
    : `${window.location.origin}/api`;
})();

class ApiService {
  private auctionCache = new Map<string, { data: any; timestamp: number }>()
  private pendingRequests = new Map<string, Promise<any>>()
  private readonly CACHE_DURATION = 5000 // 5 seconds cache

  private async request<T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
    const requestStartTime = Date.now();
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...options?.headers,
        },
        ...options,
      });

      if (!response.ok) {
        // Handle HTTP errors
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.error?.message) {
            errorMessage = errorData.error.message;
          }
        } catch {
          // If we can't parse the error response, use the default message
        }
        
        return {
          success: false,
          error: {
            message: errorMessage,
            code: response.status.toString()
          }
        } as ApiResponse<T>;
      }

      const data = await response.json();
      
      return data;
    } catch (error) {
      const totalTime = Date.now() - requestStartTime;
      console.error(`❌ [PERF] API request failed after ${totalTime}ms:`, error);
      // Handle network errors (server not running, connection issues, etc.)
      
      let errorMessage = 'Network error. Please check your connection and try again.';
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch')) {
          errorMessage = 'Unable to connect to the server. Please ensure the server is running and try again.';
        } else {
          errorMessage = error.message;
        }
      }
      
      return {
        success: false,
        error: {
          message: errorMessage,
          code: 'NETWORK_ERROR'
        }
      } as ApiResponse<T>;
    }
  }

  // Generic HTTP methods
  async get<T = any>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'GET',
    });
  }

  async post<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T = any>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    });
  }

  // Auth
  async requestOTP(data: MagicLinkRequestData): Promise<ApiResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/request-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Where the visitor is standing when they register. An auction on the
      // platform domain has no custom host to identify its owner, so this page
      // is the only thing that says which auctioneer brought them in - and the
      // server cannot see it any other way. Sent for sign-ins too, and ignored
      // there: an existing user's origin is already settled.
      body: JSON.stringify({ signupPath: currentSignupPath(), ...data }),
    });

    if (response.ok) {
      return { success: true, data: null };
    } else {
      return response.json();
    }
  }

  async verifyCode(data: { email: string; code: string }): Promise<ApiResponse<AuthUser>> {
    return this.request<AuthUser>('/auth/verify-code', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getMe(): Promise<ApiResponse<AuthUser>> {
    // Special handling for auth/me to avoid logging expected 401 errors
    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // Only a 401/403 means "not signed in". Anything else that failed - a 502
      // page from the proxy while the server wakes up, a 500 - says nothing
      // about the session, and the caller must not treat it as a sign-out.
      if (!response.ok && response.status !== 401 && response.status !== 403) {
        return { success: false, error: { code: 'SERVER_UNAVAILABLE', message: `HTTP ${response.status}` } };
      }

      return await response.json();
    } catch (error) {
      // Don't log network errors for auth checks - they're expected when not authenticated
      return { success: false, error: { code: 'NETWORK_ERROR', message: 'Network error' } };
    }
  }

  async getTenantInfo(): Promise<ApiResponse<TenantInfo | null>> {
    return this.request<TenantInfo | null>('/auth/tenant-info');
  }

  async logout(): Promise<ApiResponse> {
    return this.request('/auth/logout', { method: 'POST' });
  }

  // Impersonation
  async startImpersonation(userId: string): Promise<ApiResponse> {
    return this.request('/impersonation/start', {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  async stopImpersonation(): Promise<ApiResponse> {
    return this.request('/impersonation/stop', { method: 'POST' });
  }

  async toggleTestMode(enabled: boolean): Promise<ApiResponse> {
    return this.request('/impersonation/test-mode', { 
      method: 'POST',
      body: JSON.stringify({ enabled })
    });
  }

  // Auctions
  async getAuctions(params?: { ownerId?: string; status?: string; page?: number; limit?: number }): Promise<ApiResponse<{ auctions: AuctionWithItems[]; pagination: any }>> {
    const searchParams = new URLSearchParams();
    if (params?.ownerId) searchParams.append('ownerId', params.ownerId);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    
    const queryString = searchParams.toString();
    const endpoint = queryString ? `/auctions?${queryString}` : '/auctions';
    const cacheKey = endpoint;
    
    // Check cache first
    const cached = this.auctionCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data;
    }
    
    // Check if there's already a pending request for this endpoint
    const pendingRequest = this.pendingRequests.get(cacheKey);
    if (pendingRequest) {
      return await pendingRequest;
    }
    
    
    // Create and store the request promise
    const requestPromise = this.request<{ auctions: AuctionWithItems[]; pagination: any }>(endpoint)
      .then(result => {
        // Cache successful results
        if (result.success) {
          this.auctionCache.set(cacheKey, { data: result, timestamp: Date.now() });
        }
        return result;
      })
      .finally(() => {
        // Remove from pending requests when done
        this.pendingRequests.delete(cacheKey);
      });
    
    this.pendingRequests.set(cacheKey, requestPromise);
    return await requestPromise;
  }

  // Optimized endpoint for bidders - combines public and saved auctions in one call
  async getBidderAuctions(params?: { status?: string; page?: number; limit?: number }): Promise<ApiResponse<{ auctions: AuctionWithItems[]; savedAuctionIds: string[]; pagination: any }>> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    
    const queryString = searchParams.toString();
    const endpoint = queryString ? `/auctions/for-bidder?${queryString}` : '/auctions/for-bidder';
    
    return this.request<{ auctions: AuctionWithItems[]; savedAuctionIds: string[]; pagination: any }>(endpoint);
  }

  /**
   * The card the platform bills this account's auction fees to.
   *
   * Nothing to do with getPaymentMethods, which is the cards for paying as a
   * bidder: this one is the charity's own, and only the platform charges it.
   */
  async getBillingCard(): Promise<ApiResponse<{ card: any | null }>> {
    return this.request<{ card: any | null }>('/accounts/billing-card');
  }

  /** Saves a card Stripe has already vaulted, by its payment method id. */
  async saveBillingCard(paymentMethodId: string): Promise<ApiResponse<{ card: any }>> {
    return this.request<{ card: any }>('/accounts/billing-card', {
      method: 'POST',
      body: JSON.stringify({ paymentMethodId }),
    });
  }

  async removeBillingCard(): Promise<ApiResponse<{ card: null }>> {
    return this.request<{ card: null }>('/accounts/billing-card', { method: 'DELETE' });
  }

  // Get donations for an auction
  async getAuctionDonations(auctionId: string): Promise<ApiResponse<any[]>> {
    return this.request<any[]>(`/auctions/${auctionId}/donations`);
  }

  // Create a donation
  async createDonation(auctionId: string, amount: number, paymentMethodId: string, remainAnonymous?: boolean, message?: string, donorCoversFees?: boolean): Promise<ApiResponse<any>> {
    return this.request<any>(`/auctions/${auctionId}/donate`, {
      method: 'POST',
      body: JSON.stringify({ amount, paymentMethodId, remainAnonymous, message, donorCoversFees })
    });
  }

  /**
   * What a donation of this size costs, both ways round.
   *
   * The server works this out rather than the browser, so the figures shown to
   * the donor are the ones the charge is built from.
   */
  async getDonationQuote(auctionId: string, amount: number): Promise<ApiResponse<DonationQuote>> {
    return this.request<DonationQuote>(`/auctions/${auctionId}/donation-quote?amount=${encodeURIComponent(amount)}`);
  }


  async getAuctionItems(id: string, params?: { page?: number; limit?: number; isEditPage?: boolean }): Promise<ApiResponse<{ items: any[]; pagination: any }>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    
    const queryString = searchParams.toString();
    const endpoint = queryString ? `/auctions/${id}/items?${queryString}` : `/auctions/${id}/items`;
    
    const headers: Record<string, string> = {};
    if (params?.isEditPage) {
      headers['x-edit-page'] = 'true';
    }
    
    return this.request<{ items: any[]; pagination: any }>(endpoint, {
      headers: Object.keys(headers).length > 0 ? headers : undefined
    });
  }

  async getAuction(id: string, headers?: Record<string, string>): Promise<ApiResponse<AuctionWithItems>> {
    return this.request<AuctionWithItems>(`/auctions/${id}`, {
      headers
    });
  }

  // Lightweight endpoint specifically for edit pages - no bids loaded
  async getAuctionForEdit(id: string, skipCache: boolean = false): Promise<ApiResponse<AuctionWithItems>> {
    const url = skipCache ? `/auctions/${id}/edit?skipCache=true` : `/auctions/${id}/edit`;
    return this.request<AuctionWithItems>(url);
  }

  async getAuctionByUrl(url: string, livePreview: boolean = false): Promise<ApiResponse<AuctionWithItems | AuctionAccessResponse>> {
    const endpoint = `/auctions/url/${encodeURIComponent(url)}`;
    const cacheKey = `${endpoint}${livePreview ? '?preview=true' : ''}`;
    
    // Check cache first - use different cache times based on auction status
    const cached = this.auctionCache.get(cacheKey);
    // Live auctions: 15 seconds cache, Non-live: 2 minutes cache
    const cacheMaxAge = livePreview ? 15000 : 120000;
    if (cached && Date.now() - cached.timestamp < cacheMaxAge) {
      return cached.data;
    }
    
    // Check if there's already a pending request
    const pendingRequest = this.pendingRequests.get(cacheKey);
    if (pendingRequest) {
      return await pendingRequest;
    }
    
    
    const headers: Record<string, string> = livePreview ? { 'x-live-preview': 'true' } : {};
    
    // Create and store the request promise
    const requestPromise = this.request<AuctionWithItems | AuctionAccessResponse>(endpoint, { headers })
      .then(result => {
        // Cache successful results with appropriate TTL
        if (result.success) {
          this.auctionCache.set(cacheKey, { data: result, timestamp: Date.now() });
        }
        return result;
      })
      .finally(() => {
        // Remove from pending requests when done
        this.pendingRequests.delete(cacheKey);
      });
    
    this.pendingRequests.set(cacheKey, requestPromise);
    return await requestPromise;
  }

  async accessAuctionByUrl(url: string, data: { password?: string; inviteCode?: string }): Promise<ApiResponse<AuctionWithItems>> {
    return this.request<AuctionWithItems>(`/auctions/url/${encodeURIComponent(url)}/access`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createAuction(data: CreateAuctionData): Promise<ApiResponse<AuctionWithItems>> {
    return this.request<AuctionWithItems>('/auctions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createAuctionWithAuth(data: CreateAuctionData & { email: string; displayName: string }): Promise<ApiResponse<any>> {
    return this.request<any>('/auctions/create-with-auth', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async verifyAndCreateAuction(email: string, code: string): Promise<ApiResponse<AuctionWithItems & { user: any; account: any }>> {
    return this.request<AuctionWithItems & { user: any; account: any }>('/auctions/verify-and-create', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });
  }

  async updateAuction(id: string, data: UpdateAuctionData): Promise<ApiResponse<AuctionWithItems>> {
    return this.request<AuctionWithItems>(`/auctions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteAuction(id: string, confirmationName?: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`/auctions/${id}`, {
      method: 'DELETE',
      body: confirmationName ? JSON.stringify({ confirmationName }) : undefined,
    });
  }

  // Pass excludeAuctionId when checking on behalf of an existing auction, so its
  // own current slug is not reported as taken.
  async checkUrlAvailability(url: string, excludeAuctionId?: string): Promise<ApiResponse<{ available: boolean; reason?: string; message?: string }>> {
    const query = excludeAuctionId ? `?excludeAuctionId=${encodeURIComponent(excludeAuctionId)}` : '';
    return this.request<{ available: boolean; reason?: string; message?: string }>(
      `/auctions/check-url/${encodeURIComponent(url)}${query}`
    );
  }

  // Items
  async getItem(id: string): Promise<ApiResponse<ItemWithBids>> {
    return this.request<ItemWithBids>(`/items/${id}`);
  }

  async trackItemView(id: string, sessionId?: string): Promise<ApiResponse<{ viewCount: number; tracked: boolean }>> {
    return this.request<{ viewCount: number; tracked: boolean }>(`/items/${id}/track-view`, {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    });
  }

  async createItem(auctionId: string, data: CreateItemData): Promise<ApiResponse<ItemWithBids>> {
    return this.request<ItemWithBids>(`/items/auctions/${auctionId}/items`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateItem(id: string, data: Partial<UpdateItemData>): Promise<ApiResponse<ItemWithBids>> {
    // Convert undefined values to null so they survive JSON.stringify()
    // JSON.stringify() removes undefined properties, but we need them to reach the server
    const processedData = { ...data };
    for (const [key, value] of Object.entries(processedData)) {
      if (value === undefined) {
        (processedData as any)[key] = null;
      }
    }
    
    return this.request<ItemWithBids>(`/items/${id}`, {
      method: 'PUT',
      body: JSON.stringify(processedData),
    });
  }

  /**
   * Pin an item to the top of its auction, or unpin it.
   *
   * Its own endpoint rather than a field on updateItem: that route refuses any
   * edit to an item with bids, and a popular item is exactly the one worth
   * featuring.
   */
  async setItemFeatured(id: string, featured: boolean): Promise<ApiResponse<Item>> {
    return this.request<Item>(`/items/${id}/featured`, {
      method: 'PUT',
      body: JSON.stringify({ featured }),
    });
  }

  async deleteItem(id: string): Promise<ApiResponse> {
    return this.request(`/items/${id}`, {
      method: 'DELETE',
    });
  }

  async reorderItems(auctionId: string, itemIds: string[]): Promise<ApiResponse<{ message: string; items: Item[] }>> {
    return this.request<{ message: string; items: Item[] }>(`/items/auctions/${auctionId}/reorder`, {
      method: 'PUT',
      body: JSON.stringify({ itemIds }),
    });
  }

  async bulkDeleteItems(itemIds: string[]): Promise<ApiResponse<{ deletedCount: number; deletedItemIds: string[] }>> {
    return this.request('/items/bulk', {
      method: 'DELETE',
      body: JSON.stringify({ itemIds }),
    });
  }

  async bulkReformatDescriptions(itemIds: string[]): Promise<ApiResponse<{ updatedCount: number; updatedItems: Array<{ id: string; originalDescription: string; reformattedDescription: string }> }>> {
    return this.request('/items/bulk-reformat-descriptions', {
      method: 'POST',
      body: JSON.stringify({ itemIds }),
    });
  }

  async markItemAsSold(itemId: string): Promise<ApiResponse<Item>> {
    return this.request(`/items/${itemId}/mark-sold`, {
      method: 'POST',
    });
  }

  async revertItemToLive(itemId: string): Promise<ApiResponse<Item>> {
    return this.request(`/items/${itemId}/revert-to-live`, {
      method: 'POST',
    });
  }

  /**
   * Puts a closed item back up for bidding with a new closing time.
   *
   * The time is sent as an ISO instant, not a wall-clock string: the auctioneer
   * picks it in the auction's timezone and the caller resolves it before this.
   */
  async reopenItem(itemId: string, itemEndDate: Date): Promise<ApiResponse<Item>> {
    return this.request(`/items/${itemId}/reopen`, {
      method: 'POST',
      body: JSON.stringify({ itemEndDate: itemEndDate.toISOString() }),
    });
  }

  async getItemPaymentStatus(itemId: string): Promise<ApiResponse<{ 
    isPaid: boolean; 
    paymentStatus: 'pending' | 'paid' | 'failed';
    transaction: Transaction | null;
    allTransactions: Transaction[];
  }>> {
    return this.request<{ 
      isPaid: boolean; 
      paymentStatus: 'pending' | 'paid' | 'failed';
      transaction: Transaction | null;
      allTransactions: Transaction[];
    }>(`/items/${itemId}/payment-status`);
  }

  async donateCharityFee(itemId: string, amount: number): Promise<ApiResponse<any>> {
    return this.request(`/items/${itemId}/donate-charity-fee`, {
      method: 'POST',
      body: JSON.stringify({ amount }),
    });
  }

  // Get winner payment methods for online bidder
  async getWinnerPaymentMethods(itemId: string): Promise<ApiResponse<any>> {
    return this.request(`/items/${itemId}/winner-payment-methods`, {
      method: 'GET',
    });
  }

  async awardItemToOnlineBidder(itemId: string, paymentMethodId?: string): Promise<ApiResponse<Item>> {
    return this.request(`/items/${itemId}/award-to-online-bidder`, {
      method: 'POST',
      ...(paymentMethodId && { body: JSON.stringify({ paymentMethodId }) })
    });
  }

  async processItemPayment(itemId: string, paymentMethodId: string): Promise<ApiResponse<{
    item: Item;
    transaction: any;
  }>> {
    return this.request(`/items/${itemId}/process-payment`, {
      method: 'POST',
      body: JSON.stringify({ paymentMethodId })
    });
  }

  async bulkProcessItemPayments(itemIds: string[]): Promise<ApiResponse<{
    totalProcessed: number;
    successCount: number;
    errorCount: number;
    results: Array<{
      itemId: string;
      title: string;
      success: boolean;
      message: string;
    }>;
  }>> {
    return this.request('/items/bulk-process-payments', {
      method: 'POST',
      body: JSON.stringify({ itemIds })
    });
  }

  async awardItemToLiveBidder(itemId: string, data: {
    amount: number;
    buyerName: string;
    buyerEmail?: string;
    paymentMethod?: string;
    notes?: string;
  }): Promise<ApiResponse<{
    item: Item;
    transaction: {
      id: string;
      amount: number;
      description: string;
      buyerName: string;
      buyerEmail?: string;
      paymentMethod?: string;
      notes?: string;
    };
  }>> {
    return this.request(`/items/${itemId}/award-to-live-bidder`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async awardItemToLiveBidderWithCard(itemId: string, data: {
    amount: number;
    buyerName: string;
    buyerEmail: string;
    paymentMethodId: string;
    notes?: string;
    buyerAccountId?: string;
  }): Promise<ApiResponse<{
    item?: Item;
    transaction: {
      id: string;
      amount: number;
      currency: string;
      status: string;
      paymentIntentId: string;
      buyerName: string;
      buyerEmail: string;
      paymentMethod: string;
      notes?: string;
    };
    paymentIntent: {
      id: string;
      status: string;
      clientSecret?: string;
    };
    requiresConfirmation?: boolean;
  }>> {
    return this.request(`/items/${itemId}/award-to-live-bidder-with-card`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Bids
  async createBid(itemId: string, data: CreateBidData): Promise<ApiResponse> {
    return this.request(`/bids`, {
      method: 'POST',
      body: JSON.stringify({ ...data, itemId }),
    });
  }

  async buyNow(itemId: string, includeCharityFee: boolean = false): Promise<ApiResponse> {
    return this.request(`/items/${itemId}/buy-now`, {
      method: 'POST',
      body: JSON.stringify({ includeCharityFee }),
    });
  }

  async reservePurchase(itemId: string, includeCharityFee: boolean = false): Promise<ApiResponse> {
    return this.request(`/items/${itemId}/reserve-purchase`, {
      method: 'POST',
      body: JSON.stringify({ includeCharityFee }),
    });
  }

  async declineReservePurchase(itemId: string): Promise<ApiResponse> {
    return this.request(`/items/${itemId}/decline-reserve`, {
      method: 'POST',
    });
  }

  async removeTopBid(itemId: string): Promise<ApiResponse> {
    return this.request(`/bids/items/${itemId}/top`, {
      method: 'DELETE',
    });
  }

  async adjustBidAmount(bidId: string, newAmount: number): Promise<ApiResponse> {
    return this.request(`/bids/${bidId}/adjust`, {
      method: 'PUT',
      body: JSON.stringify({ amount: newAmount }),
    });
  }

  // Development mode helpers
  async createDevPaymentMethod(): Promise<ApiResponse> {
    return this.request('/stripe/dev-payment-method', {
      method: 'POST',
    });
  }

  async startAuction(auctionId: string): Promise<ApiResponse> {
    return this.request(`/auctions/${auctionId}/start`, {
      method: 'POST',
    });
  }

  async extendAuction(auctionId: string, newEndTime: string): Promise<ApiResponse> {
    return this.request(`/auctions/${auctionId}/extend`, {
      method: 'POST',
      body: JSON.stringify({ newEndTime }),
    });
  }

  async getBids(params?: { userId?: string; itemId?: string; page?: number; limit?: number }): Promise<ApiResponse<{ bids: any[]; pagination: any }>> {
    const searchParams = new URLSearchParams();
    if (params?.userId) searchParams.append('userId', params.userId);
    if (params?.itemId) searchParams.append('itemId', params.itemId);
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    
    const queryString = searchParams.toString();
    const endpoint = queryString ? `/bids?${queryString}` : '/bids';
    
    return this.request<{ bids: any[]; pagination: any }>(endpoint);
  }

  async getItemBids(itemId: string): Promise<ApiResponse<any[]>> {
    const response = await this.getBids({ itemId });
    if (response.success) {
      return {
        success: true,
        data: response.data.bids || []
      };
    }
    return response as ApiResponse<any[]>;
  }

  // Items
  async getItems(params?: { auctionId?: string; status?: string; page?: number; limit?: number }): Promise<ApiResponse<{ items: ItemWithBids[]; pagination: any }>> {
    const searchParams = new URLSearchParams();
    if (params?.auctionId) searchParams.append('auctionId', params.auctionId);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    
    const queryString = searchParams.toString();
    const endpoint = queryString ? `/items?${queryString}` : '/items';
    
    return this.request<{ items: ItemWithBids[]; pagination: any }>(endpoint);
  }

  // Users
  /**
   * The account's audience: everyone who has bid on, signed up through, or
   * visited its auctions.
   *
   * Separate from getUsers because it answers a different question - those are
   * the account's own members, these are the people its auctions brought in,
   * each with an account of their own.
   */
  async getAccountBidders(): Promise<ApiResponse<AccountBidderDirectory>> {
    return this.request<AccountBidderDirectory>('/users/bidders');
  }

  /**
   * One bidder's details, for the popup an auctioneer opens from a bid.
   *
   * 404 for anybody with no connection to the account, which is the
   * authorisation answer as much as the data one.
   */
  async getAccountBidder(userId: string): Promise<ApiResponse<{ currency: string; bidder: AccountBidder }>> {
    return this.request<{ currency: string; bidder: AccountBidder }>(`/users/bidders/${userId}`);
  }

  /** Writes to one bidder, from the account, with replies going to the sender. */
  async emailAccountBidder(userId: string, subject: string, message: string): Promise<ApiResponse<{ sentTo: string }>> {
    return this.request<{ sentTo: string }>(`/users/bidders/${userId}/email`, {
      method: 'POST',
      body: JSON.stringify({ subject, message }),
    });
  }

  /**
   * How many people the account has, by role, in one request.
   *
   * The tabs used to count the rows the page happened to be holding, which
   * described the page rather than the account and read zero before the list
   * arrived.
   */
  async getUserCounts(): Promise<ApiResponse<Record<string, number>>> {
    return this.request<Record<string, number>>('/users/counts');
  }

  async getUsers(): Promise<ApiResponse<User[]>> {
    return this.request<User[]>('/users');
  }

  async getUser(userId: string): Promise<ApiResponse<User>> {
    return this.request<User>(`/users/${userId}`);
  }

  async createUser(data: {
    email: string;
    displayName: string;
    role: UserRole;
  }): Promise<ApiResponse<User>> {
    return this.request<User>('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(id: string, data: {
    displayName?: string;
    isActive?: boolean;
  }): Promise<ApiResponse<User>> {
    return this.request<User>(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteUser(id: string): Promise<ApiResponse> {
    return this.request(`/users/${id}`, {
      method: 'DELETE',
    });
  }

  async updateProfile(data: {
    displayName?: string;
    firstName?: string;
    lastName?: string;
    address?: string;
    phone?: string;
    profileImageUrl?: string;
  }): Promise<ApiResponse<User>> {
    return this.request<User>('/users/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateNotificationSettings(data: {
    emailNotifications: boolean;
    newBids: boolean;
    auctionEnding: boolean;
    outbidNotifications: boolean;
  }): Promise<ApiResponse<{ message: string; notificationSettings: any }>> {
    return this.request<{ message: string; notificationSettings: any }>('/users/notification-settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Accounts
  async getAccount(): Promise<ApiResponse<Account>> {
    return this.request<Account>('/accounts/me');
  }

  async updateAccount(data: {
    name?: string;
    customDomain?: string;
    emailDomain?: string;
    emailFromAddress?: string;
    emailFromName?: string;
    logoImageUrl?: string;
    organizationPhone?: string;
    organizationEmail?: string;
    entryVideoUrl?: string;
  }): Promise<ApiResponse<Account>> {
    const response = await this.request<Account>('/accounts/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    
    return response;
  }

  async updateAuctionDefaults(data: {
    defaultStartTime: string;
    defaultEndTime: string;
    defaultDurationDays: number;
    defaultTimezone: string;
    defaultCurrency: string;
    defaultBidIncrement: number;
    defaultAuctionType: string;
    defaultVisibility: string;
    defaultAllowSnipingExtension: boolean;
    defaultSnipingWindowSeconds: number;
    defaultExtensionSeconds: number;
    defaultMaxExtensions: number;
    defaultFraudChecksLevel: string;
    defaultPrimaryColor: string;
    defaultWebsite: string;

    defaultTaxRatePercent: number;
  }): Promise<ApiResponse<{ message: string; auctionDefaults: any }>> {
    const response = await this.request<{ message: string; auctionDefaults: any }>('/accounts/auction-defaults', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    
    return response;
  }

  async updateTrackingSettings(
    data: TrackingSettings
  ): Promise<ApiResponse<{ message: string; tracking: TrackingSettings }>> {
    return this.request<{ message: string; tracking: TrackingSettings }>('/accounts/tracking', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateMarketingSettings(
    data: MarketingSettings
  ): Promise<ApiResponse<{ message: string; marketingSettings: MarketingSettings }>> {
    return this.request<{ message: string; marketingSettings: MarketingSettings }>('/accounts/marketing-settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Marketing images

  /** The formats on offer, plus whatever brand settings the account has saved. */
  async getMarketingChannels(): Promise<ApiResponse<{
    channels: MarketingChannel[]
    settings: MarketingSettings | null
    model: string
    available: boolean
    maxImagesPerRequest: number
    /** The logo that would be composited in, or null when none is uploaded. */
    logoUrl: string | null
  }>> {
    return this.request('/marketing/channels');
  }

  async getItemMarketingAssets(itemId: string): Promise<ApiResponse<{ assets: MarketingAsset[] }>> {
    return this.request(`/marketing/items/${itemId}/assets`);
  }

  async getAuctionMarketingAssets(auctionId: string): Promise<ApiResponse<{ assets: MarketingAsset[] }>> {
    return this.request(`/marketing/auctions/${auctionId}/assets`);
  }

  /**
   * Generate promotional images for an item.
   *
   * Slow by nature - each image is a full model call - so callers should show
   * progress rather than a spinner that looks stalled.
   */
  async generateMarketingImages(
    itemId: string,
    body: {
      channels: string[]
      prompt?: string
      includeItemImages?: boolean
      includeLogo?: boolean
      quality?: MarketingImageQuality
      outputFormat?: 'png' | 'jpeg'
      variants?: number
      /** Generate one image from each of the item's photos, per format. */
      perSourceImage?: boolean
      /** Composite the item's sponsor logo in, for items that carry one. */
      includeSponsorLogo?: boolean
    }
  ): Promise<ApiResponse<{
    assets: MarketingAsset[]
    failures: { channel: string; message: string }[]
    usedItemImages: number
    usedLogo: boolean
  }>> {
    return this.request(`/marketing/items/${itemId}/generate`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /**
   * Queue a batch of marketing images. Returns as soon as the work is recorded;
   * the images appear in the library as they are produced.
   */
  /** Remove several images in one request. */
  async bulkDeleteMarketingImages(
    assetIds: string[]
  ): Promise<ApiResponse<{ deleted: number; assetIds: string[] }>> {
    return this.request('/marketing/assets/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ assetIds }),
    });
  }

  /**
   * Download several images as a single zip.
   *
   * Goes around `request` because the response is a binary stream, not the usual
   * JSON envelope - but keeps `credentials` so the session still authorises it.
   */
  async downloadMarketingImages(assetIds: string[]): Promise<Blob> {
    const response = await fetch(`${API_BASE_URL}/marketing/assets/download`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assetIds }),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.error?.message || `Download failed (${response.status})`);
    }

    return response.blob();
  }

  /**
   * Stop images that are queued but not yet produced.
   *
   * Omitting assetIds cancels everything still pending for the account. Only
   * pending images are affected, so this can never remove work already paid for.
   */
  async cancelMarketingBuilds(
    assetIds?: string[]
  ): Promise<ApiResponse<{ cancelled: number; assetIds: string[] }>> {
    return this.request('/marketing/cancel', {
      method: 'POST',
      body: JSON.stringify(assetIds && assetIds.length > 0 ? { assetIds } : {}),
    });
  }

  async generateMarketingBatch(body: {
    itemIds: string[]
    channels: string[]
    prompt?: string
    quality?: MarketingImageQuality
    includeLogo?: boolean
    includeSponsorLogo?: boolean
    badgeTimeRemaining?: boolean
    badgePrice?: boolean
    badgeCustomText?: string
  }): Promise<ApiResponse<{ queued: number; items: number; assetIds: string[] }>> {
    return this.request('/marketing/generate-batch', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /**
   * Auctions this account owns. Distinct from getAuctions, which also returns
   * other organisations' public auctions - fine for browsing, wrong anywhere
   * the list is a set of things to act on.
   */
  async getMarketingAuctions(): Promise<ApiResponse<{
    auctions: { id: string; title: string; status: string }[]
  }>> {
    return this.request('/marketing/auctions');
  }

  /** The account's marketing library, newest first. */
  async getMarketingAssets(params?: {
    auctionId?: string
    itemId?: string
    limit?: number
    skip?: number
  }): Promise<ApiResponse<{ assets: MarketingAssetWithContext[]; total: number }>> {
    const searchParams = new URLSearchParams();
    if (params?.auctionId) searchParams.append('auctionId', params.auctionId);
    if (params?.itemId) searchParams.append('itemId', params.itemId);
    if (params?.limit) searchParams.append('limit', String(params.limit));
    if (params?.skip) searchParams.append('skip', String(params.skip));

    const query = searchParams.toString();
    return this.request(`/marketing/assets${query ? `?${query}` : ''}`);
  }

  async editMarketingImage(
    assetId: string,
    body: { prompt: string; quality?: MarketingImageQuality }
  ): Promise<ApiResponse<{ asset: MarketingAsset }>> {
    return this.request(`/marketing/assets/${assetId}/edit`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async deleteMarketingImage(assetId: string): Promise<ApiResponse<{ deleted: boolean }>> {
    return this.request(`/marketing/assets/${assetId}`, { method: 'DELETE' });
  }

  /**
   * Same-origin URL for an asset's bytes.
   *
   * Downloading and clipboard-copying both need the image to come from our own
   * origin: browsers ignore `download` on a cross-origin link, and reading the
   * blob straight from the bucket would need CORS configured on it.
   */
  marketingAssetFileUrl(assetId: string, options: { download?: boolean } = {}): string {
    return `${API_BASE_URL}/marketing/assets/${assetId}/file${options.download ? '?download=1' : ''}`;
  }

  /**
   * Pixels to load for the page the visitor is on. Resolved from the custom
   * domain when there is one, otherwise from the auction's owning account.
   */
  async getPublicTracking(accountId?: string): Promise<ApiResponse<{ tracking: TrackingSettings | null }>> {
    const query = accountId ? `?accountId=${encodeURIComponent(accountId)}` : '';
    return this.request<{ tracking: TrackingSettings | null }>(`/accounts/public/tracking${query}`);
  }

  async verifyDomain(domain: string): Promise<ApiResponse<{ verified: boolean; message: string; account?: Account }>> {
    return this.request<{ verified: boolean; message: string; account?: Account }>('/accounts/verify-domain', {
      method: 'POST',
      body: JSON.stringify({ domain }),
    });
  }

  async getDomainInstructions(): Promise<ApiResponse<{
    domain: string;
    verificationToken: string;
    instructions: {
      step1: { title: string; description: string; record: any };
      step2: { title: string; description: string; record: any };
      step3: { title: string; description: string };
    };
  }>> {
    return this.request('/accounts/domain-instructions');
  }

  async checkDomainNow(): Promise<ApiResponse<{
    domain: string;
    verified: boolean;
    message: string;
    account: Partial<Account>;
    /** CNAME is valid but points at a previous platform address */
    usingLegacyTarget?: boolean;
    migrationNotice?: string;
    expectedCnameTarget?: string;
  }>> {
    return this.request('/accounts/check-domain-now', {
      method: 'POST',
    });
  }

  async getDomainDebugInfo(): Promise<ApiResponse<{
    domain: string;
    expected: any;
    actual: any;
    instructions: any;
  }>> {
    return this.request('/accounts/debug-dns');
  }

  async renewCertificate(): Promise<ApiResponse<{ message: string; status: string }>> {
    return this.request<{ message: string; status: string }>('/accounts/renew-certificate', {
      method: 'POST',
    });
  }

  async deleteCertificate(): Promise<ApiResponse<{ message: string; account: Partial<Account> }>> {
    return this.request<{ message: string; account: Partial<Account> }>('/accounts/delete-certificate', {
      method: 'DELETE',
    });
  }

  async generateSSLCertificate(): Promise<ApiResponse<{ message: string; account: Partial<Account> }>> {
    return this.request<{ message: string; account: Partial<Account> }>('/accounts/generate-ssl', {
      method: 'POST',
    });
  }

  async getCertificateStatus(): Promise<ApiResponse<{
    domain: string;
    databaseStatus?: SSLCertificate;
    filesystemStatus?: SSLCertificate;
    needsRenewal: boolean;
  }>> {
    return this.request('/accounts/certificate-status');
  }

  async checkDNSStatus(): Promise<ApiResponse<{
    domain: string;
    verification: {
      cnameValid: boolean;
      cnameTarget?: string;
      txtValid: boolean;
      txtValue?: string;
      errors: string[];
    };
    dnsInfo: {
      cname?: string[];
      txt?: string[];
      a?: string[];
      errors: string[];
    };
    expected: {
      cnameTarget: string;
      txtValue: string;
    };
    ready: boolean;
  }>> {
    return this.request('/accounts/check-dns');
  }

  // SSL Admin endpoints
  async getSSLAdminOverview(): Promise<ApiResponse<{
    totalAccounts: number;
    accountsWithDomains: number;
    certificates: any[];
    serverInfo: {
      certificatesDirectory: string;
      acmeEnvironment: string;
      timestamp: string;
    };
  }>> {
    return this.request('/accounts/ssl-admin');
  }

  async forceSSLRenewal(domain: string): Promise<ApiResponse<{
    message: string;
    domain: string;
    accountId: string;
    status: string;
  }>> {
    return this.request('/accounts/ssl-admin/force-renewal', {
      method: 'POST',
      body: JSON.stringify({ domain }),
    });
  }

  async getSSLLogs(): Promise<ApiResponse<{
    logs: any[];
    totalEntries: number;
    timestamp: string;
  }>> {
    return this.request('/accounts/ssl-admin/logs');
  }

  async debugSSL(domain: string): Promise<ApiResponse<any>> {
    return this.request(`/accounts/ssl-debug/${domain}`);
  }

  // Stripe Payment Methods
  async createSetupIntent(paymentMethodId?: string): Promise<ApiResponse<{
    clientSecret: string;
    setupIntentId: string;
  }>> {
    return this.request('/stripe/setup-intent', {
      method: 'POST',
      body: JSON.stringify({ paymentMethodId }),
    });
  }

  async attachPaymentMethod(paymentMethodId: string): Promise<ApiResponse<PaymentMethod>> {
    return this.request<PaymentMethod>('/stripe/payment-methods', {
      method: 'POST',
      body: JSON.stringify({ paymentMethodId }),
    });
  }

  async getPaymentMethods(): Promise<ApiResponse<PaymentMethod[]>> {
    return this.request<PaymentMethod[]>('/stripe/payment-methods');
  }

  async getUserPaymentMethods(userId: string): Promise<ApiResponse<PaymentMethod[]>> {
    return this.request<PaymentMethod[]>(`/users/${userId}/payment-methods`);
  }

  async removePaymentMethod(paymentMethodId: string): Promise<ApiResponse> {
    return this.request(`/stripe/payment-methods/${paymentMethodId}`, {
      method: 'DELETE',
    });
  }

  async setDefaultPaymentMethod(paymentMethodId: string): Promise<ApiResponse<PaymentMethod>> {
    return this.request<PaymentMethod>(`/stripe/payment-methods/${paymentMethodId}/default`, {
      method: 'PUT',
    });
  }

  async hasPaymentMethods(): Promise<boolean> {
    try {
      const response = await this.getPaymentMethods();
      return response.success && response.data.length > 0;
    } catch (error) {
      return false;
    }
  }

  // Stripe Payments
  async createPaymentIntent(itemId: string, paymentMethodId?: string): Promise<ApiResponse<{
    clientSecret: string;
    paymentIntentId: string;
    transactionId: string;
    amount: number;
    currency: string;
  }>> {
    return this.request('/stripe/payment-intents', {
      method: 'POST',
      body: JSON.stringify({ itemId, paymentMethodId }),
    });
  }

  // Stripe Invoices & Transactions
  async getInvoices(limit?: number): Promise<ApiResponse<Invoice[]>> {
    const params = limit ? `?limit=${limit}` : '';
    return this.request<Invoice[]>(`/stripe/invoices${params}`);
  }

  async getTransactions(limit?: number): Promise<ApiResponse<Transaction[]>> {
    const params = limit ? `?limit=${limit}` : '';
    return this.request<Transaction[]>(`/stripe/transactions${params}`);
  }

  async getTransactionsOptimized(params?: { page?: number; limit?: number }): Promise<ApiResponse<{ transactions: Transaction[]; pagination: any }>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    
    const queryString = searchParams.toString();
    const endpoint = queryString ? `/stripe/transactions/optimized?${queryString}` : '/stripe/transactions/optimized';
    
    return this.request<{ transactions: Transaction[]; pagination: any }>(endpoint);
  }

  async getAllTransactions(limit?: number): Promise<ApiResponse<Transaction[]>> {
    const params = limit ? `?limit=${limit}` : '';
    return this.request<Transaction[]>(`/stripe/transactions/all${params}`);
  }

  async getAllTransactionsOptimized(params?: { page?: number; limit?: number }): Promise<ApiResponse<{ transactions: Transaction[]; pagination: any }>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    
    const queryString = searchParams.toString();
    const endpoint = queryString ? `/stripe/transactions/all/optimized?${queryString}` : '/stripe/transactions/all/optimized';
    
    return this.request<{ transactions: Transaction[]; pagination: any }>(endpoint);
  }

  async getAuctionTransactions(auctionId: string): Promise<ApiResponse<Transaction[]>> {
    return this.request<Transaction[]>(`/auctions/${auctionId}/transactions`);
  }

  async refundTransaction(transactionId: string): Promise<ApiResponse> {
    return this.request(`/stripe/transactions/${transactionId}/refund`, {
      method: 'POST',
    });
  }

  async retryTransaction(auctionId: string, transactionId: string): Promise<ApiResponse<{ status: string; message: string }>> {
    return this.request<{ status: string; message: string }>(`/auctions/${auctionId}/transactions/${transactionId}/retry`, {
      method: 'POST',
    });
  }

  async retryTransactionWithPaymentMethod(auctionId: string, transactionId: string, paymentMethodId: string, notes?: string): Promise<ApiResponse<{ status: string; message: string }>> {
    // Use the user-accessible endpoint for regular users, admin endpoint for auctioneers
    const endpoint = `/transactions/${transactionId}/retry`;
    return this.request<{ status: string; message: string }>(endpoint, {
      method: 'POST',
      body: JSON.stringify({ 
        paymentMethodId,
        notes 
      }),
    });
  }

  async getTransactionPaymentMethods(transactionId: string): Promise<ApiResponse<{
    customer: User;
    paymentMethods: PaymentMethod[];
  }>> {
    // Use the user-accessible endpoint for regular users
    return this.request<{
      customer: User;
      paymentMethods: PaymentMethod[];
    }>(`/transactions/${transactionId}/my-payment-methods`);
  }

  async getTransactionAttempts(transactionId: string): Promise<ApiResponse<PaymentAttempt[]>> {
    return this.request<PaymentAttempt[]>(`/transactions/${transactionId}/attempts`);
  }

  // Auctioneer versions of the two calls above: the bidder's cards, and a
  // retry charged to the auctioneer's account.
  async getCustomerPaymentMethodsForTransaction(transactionId: string): Promise<ApiResponse<{
    customer: User;
    paymentMethods: PaymentMethod[];
  }>> {
    return this.request<{
      customer: User;
      paymentMethods: PaymentMethod[];
    }>(`/transactions/${transactionId}/payment-methods`);
  }

  async retryAuctionTransactionWithPaymentMethod(auctionId: string, transactionId: string, paymentMethodId: string, notes?: string): Promise<ApiResponse<{ status: string; message: string }>> {
    return this.request<{ status: string; message: string }>(`/auctions/${auctionId}/transactions/${transactionId}/retry`, {
      method: 'POST',
      body: JSON.stringify({ paymentMethodId, notes }),
    });
  }

  async updateWinnerAddress(auctionId: string, winnerId: string, address: string): Promise<ApiResponse<{
    winnerId: string;
    address: string;
    winnerType: 'live' | 'online';
    userUpdated: boolean;
    message: string;
  }>> {
    return this.request(`/auctions/${auctionId}/winners/${winnerId}/address`, {
      method: 'PUT',
      body: JSON.stringify({ address }),
    });
  }

  async getAuctionFundraisingTotals(auctionId: string, bustCache = false): Promise<ApiResponse<{
    actualTotalRaised: number; // Net amount after fees
    totalCharityFees: number;
    estimatedTotal: number; // Net amount after fees
    successfulTransactionCount: number;
    charityFeeCompensationCount: number;
    goalAmount: number;
    progressPercentage: number;
    charityBeneficiary?: string;
    currency: string;
    grossActualTotalRaised?: number; // Gross amount before fees
    grossEstimatedTotal?: number; // Gross amount before fees
  } | null>> {
    const endpoint = `/auctions/${auctionId}/fundraising${bustCache ? `?t=${Date.now()}` : ''}`;
    return this.request(endpoint);
  }

  async createManualTransaction(auctionId: string, data: {
    amount: number;
    currency?: string;
    description: string;
    itemId?: string;
    type?: string;
    paymentMethod?: string;
    buyerName: string;
    buyerEmail?: string;
    notes?: string;
  }): Promise<ApiResponse<Transaction>> {
    return this.request<Transaction>(`/auctions/${auctionId}/manual-transaction`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async voidTransaction(auctionId: string, transactionId: string, reason?: string): Promise<ApiResponse<Transaction>> {
    return this.request<Transaction>(`/auctions/${auctionId}/transactions/${transactionId}/void`, {
      method: 'PUT',
      body: JSON.stringify({ reason }),
    });
  }

  async deleteTransaction(auctionId: string, transactionId: string): Promise<ApiResponse> {
    return this.request(`/auctions/${auctionId}/transactions/${transactionId}`, {
      method: 'DELETE',
    });
  }


  // User auction methods
  async saveAuction(auctionId: string): Promise<ApiResponse<UserAuction>> {
    return this.request<UserAuction>('/user-auctions', {
      method: 'POST',
      body: JSON.stringify({ auctionId }),
    });
  }

  async removeSavedAuction(auctionId: string): Promise<ApiResponse> {
    return this.request(`/user-auctions/${auctionId}`, {
      method: 'DELETE',
    });
  }

  async getSavedAuctions(): Promise<ApiResponse<{ savedAuctions: UserAuctionWithDetails[] }>> {
    return this.request<{ savedAuctions: UserAuctionWithDetails[] }>('/user-auctions');
  }

  async checkAuctionSaved(auctionId: string): Promise<ApiResponse<{ isSaved: boolean; userAuction: UserAuction | null }>> {
    return this.request<{ isSaved: boolean; userAuction: UserAuction | null }>(`/user-auctions/check/${auctionId}`);
  }

  async trackAuctionVisit(auctionId: string): Promise<ApiResponse<UserAuction>> {
    return this.request<UserAuction>('/user-auctions/track-visit', {
      method: 'POST',
      body: JSON.stringify({ auctionId }),
    });
  }

  async markSavePromptSeen(auctionId: string): Promise<ApiResponse> {
    return this.request('/user-auctions/mark-prompt-seen', {
      method: 'POST',
      body: JSON.stringify({ auctionId }),
    });
  }

  async updateAuctionAccess(auctionId: string): Promise<ApiResponse<UserAuction>> {
    return this.request<UserAuction>(`/user-auctions/access/${auctionId}`, {
      method: 'POST',
    });
  }

  async getAuctionViewCounts(auctionId: string): Promise<ApiResponse<{ [itemId: string]: number }>> {
    return this.request<{ [itemId: string]: number }>(`/auctions/${auctionId}/view-counts`);
  }

  async getAuctionWinners(auctionId: string): Promise<ApiResponse<any[]>> {
    return this.request<any[]>(`/auctions/${auctionId}/winners`);
  }

  async getAuctionWinnersLightweight(auctionId: string, page: number = 1, limit: number = 20): Promise<ApiResponse<{
    data: any[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  }>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });
    return this.request<{
      data: any[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
        hasNext: boolean;
        hasPrev: boolean;
      };
    }>(`/auctions/${auctionId}/winners/lightweight?${params}`);
  }

  async getAuctionCounts(auctionId: string): Promise<ApiResponse<{
    transactions: number;
    winners: number;
    raffles: number;
  }>> {
    return this.request<{
      transactions: number;
      winners: number;
      raffles: number;
    }>(`/auctions/${auctionId}/counts`);
  }

  async sendWinnersEmail(auctionId: string, data: {
    winnerIds: string[];
    subject: string;
    message: string;
  }): Promise<ApiResponse<{
    totalSent: number;
    totalFailed: number;
    successful: Array<{winnerId: string; email: string; success: boolean}>;
    failed: Array<{winnerId?: string; email?: string; success: boolean; error: string}>;
  }>> {
    return this.request<{
      totalSent: number;
      totalFailed: number;
      successful: Array<{winnerId: string; email: string; success: boolean}>;
      failed: Array<{winnerId?: string; email?: string; success: boolean; error: string}>;
    }>(`/auctions/${auctionId}/winners/send-email`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async resendWinningEmail(auctionId: string, winnerId: string): Promise<ApiResponse<{
    winnerId: string;
    email: string;
  }>> {
    return this.request<{
      winnerId: string;
      email: string;
    }>(`/auctions/${auctionId}/winners/${winnerId}/resend-won-email`, {
      method: 'POST',
    });
  }

  // Item offers: an auctioneer offers an item to one person at a set price by link
  async createItemOffer(itemId: string, data: { email: string; amount: number; expiresInDays?: number }): Promise<ApiResponse<{
    offer: ItemOffer;
    url: string;
    emailSent: boolean;
  }>> {
    return this.request(`/offers/items/${itemId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getItemOffers(itemId: string): Promise<ApiResponse<ItemOffer[]>> {
    return this.request<ItemOffer[]>(`/offers/items/${itemId}`);
  }

  async cancelItemOffer(offerId: string): Promise<ApiResponse<ItemOffer>> {
    return this.request<ItemOffer>(`/offers/${offerId}/cancel`, { method: 'POST' });
  }

  async resendItemOffer(offerId: string): Promise<ApiResponse<{ offer: ItemOffer; url: string; emailSent: boolean }>> {
    return this.request(`/offers/${offerId}/resend`, { method: 'POST' });
  }

  async getOfferByToken(token: string): Promise<ApiResponse<OfferForRecipient>> {
    return this.request<OfferForRecipient>(`/offers/token/${encodeURIComponent(token)}`);
  }

  async acceptOffer(token: string, paymentMethodId?: string): Promise<ApiResponse<{ offer?: ItemOffer; paymentStatus: string }>> {
    return this.request(`/offers/token/${encodeURIComponent(token)}/accept`, {
      method: 'POST',
      body: JSON.stringify({ paymentMethodId }),
    });
  }

  async declineOffer(token: string): Promise<ApiResponse<ItemOffer>> {
    return this.request<ItemOffer>(`/offers/token/${encodeURIComponent(token)}/decline`, { method: 'POST' });
  }

  // Bulk Upload
  async previewBulkUpload(auctionId: string, file: File): Promise<ApiResponse<{
    headers: string[];
    sampleData: any[];
    autoMapping: { [key: string]: string };
    totalRows: number;
    availableFields: Array<{ key: string; label: string; required: boolean }>;
  }>> {
    const formData = new FormData();
    formData.append('file', file);

    return this.request(`/items/auctions/${auctionId}/bulk-upload/preview`, {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set Content-Type for FormData
    });
  }

  async processBulkUpload(auctionId: string, file: File, headerMapping: { [key: string]: string }): Promise<Response> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('headerMapping', JSON.stringify(headerMapping));

    // Use the same cookie configuration as the main request method
    return fetch(`${API_BASE_URL}/items/auctions/${auctionId}/bulk-upload`, {
      method: 'POST',
      credentials: 'include', // This is the key - same as main request method
      body: formData,
      // Don't set any headers - let browser handle FormData Content-Type
    });
  }

  // Raffle item bulk upload methods
  async previewRaffleBulkUpload(raffleId: string, file: File): Promise<ApiResponse<{
    headers: string[];
    sampleData: any[];
    autoMapping: { [key: string]: string };
    totalRows: number;
    availableFields: Array<{ key: string; label: string; required: boolean }>;
  }>> {
    const formData = new FormData();
    formData.append('file', file);

    // Use direct fetch instead of this.request to avoid JSON Content-Type header
    const response = await fetch(`${API_BASE_URL}/raffle-items/raffles/${raffleId}/bulk-upload/preview`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
      // Don't set Content-Type header - let browser handle FormData
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    return response.json();
  }

  async processRaffleBulkUpload(raffleId: string, file: File, headerMapping: { [key: string]: string }): Promise<Response> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('headerMapping', JSON.stringify(headerMapping));

    // Use the same cookie configuration as the main request method
    return fetch(`${API_BASE_URL}/raffle-items/raffles/${raffleId}/bulk-upload`, {
      method: 'POST',
      credentials: 'include', // This is the key - same as main request method
      body: formData,
      // Don't set any headers - let browser handle FormData Content-Type
    });
  }

  // Stripe Connect OAuth methods
  async getStripeOAuthUrl(): Promise<ApiResponse<{ authUrl: string; redirectUri?: string; returnOrigin?: string }>> {
    return this.request<{ authUrl: string; redirectUri?: string; returnOrigin?: string }>('/stripe/connect/oauth/authorize');
  }

  async handleStripeOAuthCallback(code: string, state?: string): Promise<ApiResponse<{ accountId: string; chargesEnabled: boolean; status: string }>> {
    return this.request<{ accountId: string; chargesEnabled: boolean; status: string }>('/stripe/connect/oauth/callback', {
      method: 'POST',
      body: JSON.stringify({ code, state }),
    });
  }

  async disconnectStripeOAuth(): Promise<ApiResponse<{ disconnected: boolean; deauthorized: boolean; note?: string }>> {
    return this.request<{ disconnected: boolean; deauthorized: boolean; note?: string }>('/stripe/connect/oauth/disconnect', {
      method: 'DELETE',
    });
  }

  /** Express dashboard link so a connected auctioneer can manage payouts */
  async getStripeDashboardLink(): Promise<ApiResponse<{ url: string }>> {
    return this.request<{ url: string }>('/stripe/connect/dashboard-link', {
      method: 'POST',
    });
  }

  // Raffle methods
  async getRaffles(auctionId: string): Promise<ApiResponse<any[]>> {
    return this.request<any[]>(`/raffles/auctions/${auctionId}`);
  }

  async getRaffle(id: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/raffles/${id}`);
  }

  async createRaffle(auctionId: string, data: {
    title: string;
    description?: string;
    images?: string[];
    mainImageIndex?: number;
    ticketPacks: Array<{
      id: string;
      tickets: number;
      price: number;
      description?: string;
    }>;
    totalTickets: number;
    drawDate?: Date;
  }): Promise<ApiResponse<any>> {
    return this.request<any>(`/raffles/auctions/${auctionId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateRaffle(id: string, data: {
    title?: string;
    description?: string;
    images?: string[];
    mainImageIndex?: number;
    ticketPacks?: Array<{
      id: string;
      tickets: number;
      price: number;
      description?: string;
    }>;
    totalTickets?: number;
    drawDate?: Date;
    status?: string;
  }): Promise<ApiResponse<any>> {
    return this.request<any>(`/raffles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteRaffle(id: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`/raffles/${id}`, {
      method: 'DELETE',
    });
  }

  async purchaseRaffleTickets(raffleId: string, data: {
    packId: string;
    quantity: number;
    paymentMethodId: string;
  }): Promise<ApiResponse<{
    transaction: any;
    tickets?: any[];
    paymentIntent: {
      id: string;
      status: string;
      clientSecret: string;
    };
  }>> {
    return this.request<{
      transaction: any;
      tickets?: any[];
      paymentIntent: {
        id: string;
        status: string;
        clientSecret: string;
      };
    }>(`/raffles/${raffleId}/purchase`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getRaffleTickets(raffleId: string): Promise<ApiResponse<any[]>> {
    return this.request<any[]>(`/raffles/${raffleId}/tickets`);
  }

  async getAllRaffleTickets(raffleId: string): Promise<ApiResponse<any[]>> {
    return this.request<any[]>(`/raffles/${raffleId}/all-tickets`);
  }

  async drawRaffleWinner(raffleId: string): Promise<ApiResponse<{
    raffle: any;
    winningTicket: any;
    winner: {
      id: string;
      displayName: string;
      email: string;
    };
  }>> {
    return this.request<{
      raffle: any;
      winningTicket: any;
      winner: {
        id: string;
        displayName: string;
        email: string;
      };
    }>(`/raffles/${raffleId}/draw`, {
      method: 'POST',
    });
  }

  // Raffle Item methods
  async getRaffleItems(raffleId?: string): Promise<ApiResponse<{
    raffleItems: any[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }>> {
    const params = new URLSearchParams();
    if (raffleId) {
      params.append('raffleId', raffleId);
    }
    const queryString = params.toString();
    return this.request<{
      raffleItems: any[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        pages: number;
      };
    }>(`/raffle-items${queryString ? `?${queryString}` : ''}`);
  }

  async getRaffleItem(id: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/raffle-items/${id}`);
  }

  async createRaffleItem(raffleId: string, raffleItemData: any): Promise<ApiResponse<any>> {
    return this.request<any>(`/raffle-items/raffles/${raffleId}/items`, {
      method: 'POST',
      body: JSON.stringify(raffleItemData),
    });
  }

  async updateRaffleItem(id: string, updates: any): Promise<ApiResponse<any>> {
    return this.request<any>(`/raffle-items/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteRaffleItem(id: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`/raffle-items/${id}`, {
      method: 'DELETE',
    });
  }

  async bulkDeleteRaffleItems(raffleItemIds: string[]): Promise<ApiResponse<{
    deletedCount: number;
    deletedRaffleItemIds: string[];
  }>> {
    return this.request<{
      deletedCount: number;
      deletedRaffleItemIds: string[];
    }>('/raffle-items/bulk', {
      method: 'DELETE',
      body: JSON.stringify({ raffleItemIds }),
    });
  }

  async reorderRaffleItems(raffleId: string, raffleItemIds: string[]): Promise<ApiResponse<{
    message: string;
    raffleItems: any[];
  }>> {
    return this.request<{
      message: string;
      raffleItems: any[];
    }>(`/raffle-items/raffles/${raffleId}/reorder`, {
      method: 'PUT',
      body: JSON.stringify({ raffleItemIds }),
    });
  }

  // Ticket allocation methods
  async allocateRaffleTickets(raffleId: string, allocations: { itemId: string; ticketCount: number }[]): Promise<ApiResponse<{
    allocatedTickets: number;
  }>> {
    return this.request<{
      allocatedTickets: number;
    }>(`/raffles/${raffleId}/allocate-tickets`, {
      method: 'POST',
      body: JSON.stringify({ allocations }),
    });
  }

  async luckyDrawRaffleTickets(raffleId: string): Promise<ApiResponse<{
    allocatedTickets: number;
    allocations: { itemId: string; ticketCount: number; itemTitle?: string }[];
  }>> {
    return this.request<{
      allocatedTickets: number;
      allocations: { itemId: string; ticketCount: number; itemTitle?: string }[];
    }>(`/raffles/${raffleId}/lucky-draw`, {
      method: 'POST',
    });
  }

  async getRaffleTicketAllocations(raffleId: string): Promise<ApiResponse<{
    allocations: { item: any; ticketCount: number; tickets: any[] }[];
    unallocatedTickets: any[];
    totalTickets: number;
  }>> {
    return this.request<{
      allocations: { item: any; ticketCount: number; tickets: any[] }[];
      unallocatedTickets: any[];
      totalTickets: number;
    }>(`/raffles/${raffleId}/ticket-allocations`);
  }

  // Admin ticket allocation methods
  async adminAllocateRaffleTickets(raffleId: string, data: { allocations: Record<string, string[]> }): Promise<ApiResponse<{
    allocatedTickets: number;
  }>> {
    return this.request<{
      allocatedTickets: number;
    }>(`/raffles/${raffleId}/admin-allocate-tickets`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async adminLuckyDrawRaffleTickets(raffleId: string, data: { ticketIds: string[] }): Promise<ApiResponse<{
    allocatedTickets: number;
    allocations: { itemId: string; itemTitle: string; ticketCount: number }[];
  }>> {
    return this.request<{
      allocatedTickets: number;
      allocations: { itemId: string; itemTitle: string; ticketCount: number }[];
    }>(`/raffles/${raffleId}/admin-lucky-draw`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async sendWinnerEmail(raffleId: string, data: {
    itemId: string;
    winnerId: string;
    winnerEmail: string;
    winnerName: string;
    itemTitle: string;
    itemDescription?: string;
    ticketNumber: string;
  }): Promise<ApiResponse<{
    message: string;
    recipient: string;
    item: string;
  }>> {
    return this.post(`/raffles/${raffleId}/send-winner-email`, data);
  }

  // Email Template methods
  async getEmailTemplates(): Promise<ApiResponse<{ templates: any[] }>> {
    return this.get('/notifications/email-templates');
  }

  async createEmailTemplate(data: any): Promise<ApiResponse<any>> {
    return this.post('/notifications/email-templates', data);
  }

  async updateEmailTemplate(id: string, data: any): Promise<ApiResponse<any>> {
    return this.put(`/notifications/email-templates/${id}`, data);
  }

  async revertEmailTemplateToDefault(id: string): Promise<ApiResponse<any>> {
    return this.post(`/notifications/email-templates/${id}/revert-to-default`);
  }

  async sendTestEmail(id: string): Promise<ApiResponse<any>> {
    return this.post(`/notifications/email-templates/${id}/send-test`);
  }

  // Email verification methods
  async sendEmailVerification(emailAddress: string): Promise<ApiResponse<any>> {
    return this.post('/accounts/send-email-verification', { emailAddress });
  }

  async verifyEmailCode(verificationCode: string): Promise<ApiResponse<any>> {
    return this.post('/accounts/verify-email-code', { verificationCode });
  }

  // Activity methods
  async getUserActivity(): Promise<ApiResponse<{
    totalDonated: number;
    currentBids: any[];
    wonItems: any[];
    totalCurrentBids: number;
    totalWonItems: number;
    charityBeneficiaries: string[];
  }>> {
    return this.request('/activity');
  }

  // Payment verification methods
  async getPendingVerificationTransactions(auctionId: string): Promise<ApiResponse<{ transactions: Transaction[]; count: number }>> {
    return this.request<{ transactions: Transaction[]; count: number }>(`/verification/auctions/${auctionId}/pending`);
  }

  async verifyTransaction(transactionId: string): Promise<ApiResponse<{ transaction: Transaction; paymentProcessed: boolean; message: string }>> {
    return this.request<{ transaction: Transaction; paymentProcessed: boolean; message: string }>(`/verification/transactions/${transactionId}/verify`, {
      method: 'POST',
    });
  }

  async rejectTransaction(transactionId: string, reason?: string): Promise<ApiResponse<{ transaction: Transaction; message: string }>> {
    return this.request<{ transaction: Transaction; message: string }>(`/verification/transactions/${transactionId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async bulkVerifyTransactions(transactionIds: string[]): Promise<ApiResponse<{ results: any[]; errors: any[]; totalProcessed: number; totalErrors: number; message: string }>> {
    return this.request<{ results: any[]; errors: any[]; totalProcessed: number; totalErrors: number; message: string }>(`/verification/transactions/bulk-verify`, {
      method: 'POST',
      body: JSON.stringify({ transactionIds }),
    });
  }

  async bulkRejectTransactions(transactionIds: string[], reason?: string): Promise<ApiResponse<{ results: any[]; errors: any[]; totalRejected: number; totalErrors: number; message: string }>> {
    return this.request<{ results: any[]; errors: any[]; totalRejected: number; totalErrors: number; message: string }>(`/verification/transactions/bulk-reject`, {
      method: 'POST',
      body: JSON.stringify({ transactionIds, reason }),
    });
  }
}

export const apiService = new ApiService();
